import { useState } from 'react';
import { KIcon } from './KIcon.js';
import { apiFetch } from '../hooks/useApi.js';

interface PortaalLink { portaalUrl: string; verlooptOp: string }

/**
 * Toont en kopieert de persoonlijke klantportaal-link van een dossier, zodat een verkoper
 * die zelf via WhatsApp, sms of mail kan doorgeven. De server maakt de link aan als die
 * ontbreekt en verlengt ze (90 dagen); de klant logt niet in, de link is de toegang.
 */
export function PortaalLinkKnop({ dossierId, className }: { dossierId: string; className?: string }) {
  const [link, setLink] = useState<PortaalLink | null>(null);
  const [toestand, setToestand] = useState<'rust' | 'bezig' | 'gekopieerd' | 'kopieer-zelf' | 'fout'>('rust');

  const haalEnKopieer = async () => {
    setToestand('bezig');
    try {
      const l = link ?? await apiFetch<PortaalLink>('/api/klantportaal/generate-link', {
        method: 'POST',
        body: JSON.stringify({ dossierId }),
      });
      setLink(l);
      try {
        await navigator.clipboard.writeText(l.portaalUrl);
        setToestand('gekopieerd');
      } catch {
        setToestand('kopieer-zelf');
      }
    } catch {
      setToestand('fout');
    }
  };

  const verloopt = link ? new Date(link.verlooptOp).toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <div className={className}>
      <button type="button" className="k-button k-button--secondary" onClick={haalEnKopieer} disabled={toestand === 'bezig'} aria-busy={toestand === 'bezig'}>
        <KIcon naam={toestand === 'gekopieerd' ? 'vink' : 'documenten'} />
        {toestand === 'bezig' ? 'Bezig…' : toestand === 'gekopieerd' ? 'Portaallink gekopieerd' : 'Portaallink kopiëren'}
      </button>
      {link && (
        <p className="mt-2 text-[13px] leading-5 text-[var(--k-muted)]" role="status">
          {toestand === 'kopieer-zelf' ? 'Kopiëren lukte niet automatisch. Selecteer de link: ' : 'Link voor de klant, geldig tot ' + verloopt + ': '}
          <input
            readOnly
            value={link.portaalUrl}
            aria-label="Portaallink voor de klant"
            onFocus={(e) => e.currentTarget.select()}
            className="mt-1 block w-full rounded-lg border border-[var(--k-border)] bg-white px-3 py-2 text-[13px] text-[var(--k-text)]"
          />
        </p>
      )}
      {toestand === 'fout' && (
        <p className="mt-2 text-[13px] leading-5 text-[var(--k-danger)]" role="alert">De link kon niet worden aangemaakt. Probeer het opnieuw.</p>
      )}
    </div>
  );
}
